// Procedural WebAudio SFX. No samples required — every sound is synthesized
// from oscillators + envelopes on demand. The AudioContext is lazily created
// on the first user gesture (browser autoplay policy).

const AMBIENT_VOLUME = 0.35;

export default class Audio {
  constructor() {
    this.ctx = null;
    this.master = null;
    this.muted = false;
    this.ambient = null;
    this._ambientWantsPlay = false;
    this._unlock = this._unlock.bind(this);
    window.addEventListener("pointerdown", this._unlock, { once: true });
    window.addEventListener("keydown", this._unlock, { once: true });
    window.addEventListener("touchstart", this._unlock, { once: true });
  }

  _unlock() {
    if (!this.ctx) {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (Ctx) {
        this.ctx = new Ctx();
        this.master = this.ctx.createGain();
        this.master.gain.value = 0.4;
        this.master.connect(this.ctx.destination);
      }
    }
    if (this._ambientWantsPlay) this._startAmbient();
  }

  loadAmbient(url) {
    if (this.ambient) return;
    const el = new window.Audio(url);
    el.loop = true;
    el.preload = "auto";
    el.volume = this.muted ? 0 : AMBIENT_VOLUME;
    this.ambient = el;
  }

  playAmbient() {
    this._ambientWantsPlay = true;
    this._startAmbient();
  }

  pauseAmbient() {
    this._ambientWantsPlay = false;
    if (this.ambient) this.ambient.pause();
  }

  _startAmbient() {
    if (!this.ambient) return;
    if (this.muted) return;
    const p = this.ambient.play();
    if (p && typeof p.catch === "function") p.catch(() => {});
  }

  setMuted(m) {
    this.muted = m;
    if (this.master) this.master.gain.value = m ? 0 : 0.4;
    if (this.ambient) this.ambient.volume = m ? 0 : AMBIENT_VOLUME;
    if (m && this.ambient) this.ambient.pause();
    else if (!m && this._ambientWantsPlay) this._startAmbient();
  }

  _ready() {
    if (this.muted) return false;
    if (!this.ctx) return false;
    if (this.ctx.state === "suspended") this.ctx.resume();
    return true;
  }

  _envelope(node, t0, attack, hold, release, peak = 1) {
    const g = node.gain;
    g.cancelScheduledValues(t0);
    g.setValueAtTime(0.0001, t0);
    g.exponentialRampToValueAtTime(peak, t0 + attack);
    g.setValueAtTime(peak, t0 + attack + hold);
    g.exponentialRampToValueAtTime(0.0001, t0 + attack + hold + release);
  }

  _osc(type, freq, dest) {
    const o = this.ctx.createOscillator();
    o.type = type;
    o.frequency.value = freq;
    const g = this.ctx.createGain();
    g.gain.value = 0;
    o.connect(g);
    g.connect(dest || this.master);
    return { osc: o, gain: g };
  }

  _noise(durationSec) {
    const buf = this.ctx.createBuffer(1, Math.floor(this.ctx.sampleRate * durationSec), this.ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
    const src = this.ctx.createBufferSource();
    src.buffer = buf;
    return src;
  }

  playGem() {
    if (!this._ready()) return;
    const t = this.ctx.currentTime;
    const { osc, gain } = this._osc("triangle", 880);
    osc.frequency.setValueAtTime(880, t);
    osc.frequency.exponentialRampToValueAtTime(1760, t + 0.12);
    this._envelope(gain, t, 0.005, 0.02, 0.16, 0.5);
    osc.start(t);
    osc.stop(t + 0.22);
  }

  playShoot() {
    if (!this._ready()) return;
    const t = this.ctx.currentTime;
    const { osc, gain } = this._osc("square", 520);
    osc.frequency.setValueAtTime(520, t);
    osc.frequency.exponentialRampToValueAtTime(120, t + 0.1);
    this._envelope(gain, t, 0.002, 0.01, 0.08, 0.25);
    osc.start(t);
    osc.stop(t + 0.12);
  }

  playBomb() {
    if (!this._ready()) return;
    const t = this.ctx.currentTime;
    const noise = this._noise(0.35);
    const filter = this.ctx.createBiquadFilter();
    filter.type = "lowpass";
    filter.frequency.setValueAtTime(2000, t);
    filter.frequency.exponentialRampToValueAtTime(200, t + 0.3);
    const g = this.ctx.createGain();
    g.gain.value = 0;
    noise.connect(filter);
    filter.connect(g);
    g.connect(this.master);
    this._envelope(g, t, 0.005, 0.02, 0.3, 0.7);
    noise.start(t);
    noise.stop(t + 0.35);

    // sub thump
    const { osc: sub, gain: subGain } = this._osc("sine", 90);
    sub.frequency.setValueAtTime(120, t);
    sub.frequency.exponentialRampToValueAtTime(40, t + 0.25);
    this._envelope(subGain, t, 0.005, 0.02, 0.25, 0.6);
    sub.start(t);
    sub.stop(t + 0.3);
  }

  playJump() {
    if (!this._ready()) return;
    const t = this.ctx.currentTime;
    const { osc, gain } = this._osc("sine", 440);
    osc.frequency.setValueAtTime(330, t);
    osc.frequency.exponentialRampToValueAtTime(660, t + 0.18);
    this._envelope(gain, t, 0.005, 0.02, 0.16, 0.3);
    osc.start(t);
    osc.stop(t + 0.2);
  }

  playDeath() {
    if (!this._ready()) return;
    const t = this.ctx.currentTime;
    // descending tone + noise burst
    const { osc, gain } = this._osc("sawtooth", 260);
    osc.frequency.setValueAtTime(260, t);
    osc.frequency.exponentialRampToValueAtTime(60, t + 0.6);
    this._envelope(gain, t, 0.01, 0.05, 0.6, 0.4);
    osc.start(t);
    osc.stop(t + 0.7);

    const noise = this._noise(0.6);
    const filter = this.ctx.createBiquadFilter();
    filter.type = "bandpass";
    filter.frequency.value = 600;
    filter.Q.value = 1.2;
    const g = this.ctx.createGain();
    g.gain.value = 0;
    noise.connect(filter);
    filter.connect(g);
    g.connect(this.master);
    this._envelope(g, t, 0.01, 0.04, 0.5, 0.5);
    noise.start(t);
    noise.stop(t + 0.55);
  }

  playStart() {
    if (!this._ready()) return;
    const t = this.ctx.currentTime;
    const notes = [523, 659, 784]; // C5 E5 G5 arpeggio
    notes.forEach((f, i) => {
      const { osc, gain } = this._osc("triangle", f);
      const t0 = t + i * 0.08;
      this._envelope(gain, t0, 0.005, 0.04, 0.18, 0.35);
      osc.start(t0);
      osc.stop(t0 + 0.25);
    });
  }
}
